import { Fragment, useEffect, useState } from "react";
import DateInput from "@/components/DateInput";
import api, { fmtNum } from "@/lib/api";
import Topbar from "@/components/layout/Topbar";
import { Button } from "@/components/ui/button";
import {
  Warning, CaretDown, CaretRight, CaretLeft,
  Receipt, ShoppingCart, DownloadSimple,
} from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Orden de Compra: cruce entre stock actual y pedidos con fecha objetivo.
 * Endpoint backend: GET /api/compras/orden-compra?fecha=YYYY-MM-DD
 * Export Excel:     GET /api/compras/orden-compra/export?fecha=YYYY-MM-DD
 */
export default function OrdenCompraPage() {
  const [fecha, setFecha] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0, 10);
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [expanded, setExpanded] = useState(null); // producto_id expandido

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/compras/orden-compra?fecha=${fecha}`);
      setData(d);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al cargar la orden de compra");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [fecha]);

  const shiftDay = (delta) => {
    const d = new Date(fecha + "T12:00:00");
    d.setDate(d.getDate() + delta);
    setFecha(d.toISOString().slice(0, 10));
  };

  const exportar = async () => {
    setExporting(true);
    try {
      const resp = await api.get(`/compras/orden-compra/export?fecha=${fecha}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `orden_compra_${fecha}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("No se pudo exportar el Excel");
    } finally {
      setExporting(false);
    }
  };

  const fechaLabel = (() => {
    const d = new Date(fecha + "T12:00:00");
    return d.toLocaleDateString("es-AR", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });
  })();

  return (
    <>
      <Topbar
        title="Orden de Compra"
        subtitle="Cruce entre stock actual y demanda de pedidos futuros"
      />
      <div className="p-4 space-y-4" data-testid="orden-compra-page">
        {/* Header con selector de fecha + resumen + exportar */}
        <div className="border border-[#E5E5E5] bg-white rounded-sm p-4">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <ShoppingCart size={20} className="text-[#6B1F24]" weight="bold" />
              <div>
                <div className="font-heading font-semibold text-[15px]">Orden de Compra sugerida</div>
                <div className="text-[11px] text-[#666] font-mono uppercase tracking-wider mt-0.5">
                  {fechaLabel}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => shiftDay(-1)}
                className="h-9 w-9 p-0 rounded-sm"
                data-testid="oc-day-prev"
                title="Día anterior"
              >
                <CaretLeft size={14} weight="bold" />
              </Button>
              <DateInput
                value={fecha}
                onChange={(v) => setFecha(v)}
                className="h-9 w-40 rounded-sm text-[13px] font-mono"
                data-testid="oc-fecha"
              />
              <Button
                variant="outline"
                onClick={() => shiftDay(1)}
                className="h-9 w-9 p-0 rounded-sm"
                data-testid="oc-day-next"
                title="Día siguiente"
              >
                <CaretRight size={14} weight="bold" />
              </Button>
              <Button
                onClick={exportar}
                disabled={exporting || loading || !data}
                className="h-9 rounded-sm bg-[#6B1F24] hover:bg-[#571519] ml-2"
                data-testid="oc-export"
              >
                <DownloadSimple size={14} className="mr-1" />
                {exporting ? "Exportando…" : "Exportar Excel"}
              </Button>
            </div>
          </div>

          {data && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
              <StatBox label="Productos con faltante" value={data.resumen.productos_con_faltante} accent="rose" />
              <StatBox label="Kg a comprar" value={fmtNum(data.resumen.kg_a_comprar, 2)} accent="rose" />
              <StatBox label="Demanda total (kg)" value={fmtNum(data.resumen.kg_demanda_total, 2)} accent="blue" />
              <StatBox label="Ítems en catálogo" value={(data.faltantes.length + data.resto.length)} accent="neutral" />
            </div>
          )}
        </div>

        {/* Tabla FALTANTES */}
        <div className="border border-[#E5E5E5] bg-white rounded-sm overflow-hidden">
          <div className="px-4 py-2 border-b bg-rose-50/60 flex items-center gap-2">
            <Warning size={14} className="text-rose-700" weight="bold" />
            <span className="font-heading font-semibold text-[13px] text-rose-800">Faltantes — hay que comprar</span>
            {data && <span className="text-[11px] text-rose-600 ml-auto font-mono">{data.faltantes.length} productos</span>}
          </div>
          <OcTable
            filas={loading ? [] : (data?.faltantes || [])}
            loading={loading}
            isFaltante
            expanded={expanded}
            setExpanded={setExpanded}
            emptyMsg="Sin faltantes: todo el stock necesario está cubierto."
          />
        </div>

        {/* Tabla RESTO */}
        <div className="border border-[#E5E5E5] bg-white rounded-sm overflow-hidden">
          <div className="px-4 py-2 border-b bg-neutral-50 flex items-center gap-2">
            <Receipt size={14} className="text-[#666]" weight="bold" />
            <span className="font-heading font-semibold text-[13px] text-[#666]">Resto del catálogo (stock disponible o sin demanda)</span>
            {data && <span className="text-[11px] text-[#999] ml-auto font-mono">{data.resto.length} productos</span>}
          </div>
          <OcTable
            filas={loading ? [] : (data?.resto || [])}
            loading={loading}
            isFaltante={false}
            expanded={expanded}
            setExpanded={setExpanded}
            emptyMsg="Cargando…"
          />
        </div>
      </div>
    </>
  );
}

function StatBox({ label, value, accent = "neutral" }) {
  const bg = accent === "rose" ? "bg-rose-50 border-rose-200 text-rose-900" :
             accent === "blue" ? "bg-sky-50 border-sky-200 text-sky-900" :
             "bg-neutral-50 border-neutral-200 text-neutral-800";
  return (
    <div className={`border rounded-sm px-3 py-2 ${bg}`}>
      <div className="text-[10px] uppercase font-mono tracking-widest opacity-70">{label}</div>
      <div className="text-[18px] font-bold font-mono mt-0.5">{value}</div>
    </div>
  );
}

function OcTable({ filas, loading, isFaltante, expanded, setExpanded, emptyMsg }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[13px]">
        <thead className="text-[10px] uppercase font-mono tracking-wider text-[#666] bg-neutral-50 border-b">
          <tr>
            <th className="px-3 py-2 w-8"></th>
            <th className="px-3 py-2 text-left w-24">Código</th>
            <th className="px-3 py-2 text-left">Producto</th>
            <th className="px-3 py-2 text-right w-24">Stock</th>
            <th className="px-3 py-2 text-right w-32">Comprometido</th>
            <th className="px-3 py-2 text-right w-28">Demanda</th>
            <th className={`px-3 py-2 text-right w-28 ${isFaltante ? "text-rose-800" : "text-[#666]"}`}>
              {isFaltante ? "A COMPRAR" : "Sobrante"}
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-100">
          {loading && (
            <tr><td colSpan={7} className="px-4 py-6 text-center text-[#999] italic">Cargando…</td></tr>
          )}
          {!loading && filas.length === 0 && (
            <tr><td colSpan={7} className="px-4 py-6 text-center text-[#999] italic">{emptyMsg}</td></tr>
          )}
          {!loading && filas.map((f) => {
            const rowKey = f.producto_id || f.nombre;
            const isOpen = expanded === rowKey;
            const sobrante = (f.stock_actual || 0) - (f.demanda || 0);
            return (
              <Fragment key={rowKey}>
                <tr className={`hover:bg-neutral-50 ${isFaltante ? "bg-rose-50/30" : ""}`}>
                  <td className="px-3 py-1.5">
                    {(f.pedidos_ref || []).length > 0 && (
                      <button
                        onClick={() => setExpanded(isOpen ? null : rowKey)}
                        className="text-[#666] hover:text-[#6B1F24]"
                        data-testid={`oc-expand-${f.codigo || f.nombre}`}
                      >
                        {isOpen ? <CaretDown size={12} weight="bold" /> : <CaretRight size={12} weight="bold" />}
                      </button>
                    )}
                  </td>
                  <td className="px-3 py-1.5 font-mono text-[11px] text-[#666]">{f.codigo || "—"}</td>
                  <td className="px-3 py-1.5 font-medium">{f.nombre}</td>
                  <td className="px-3 py-1.5 text-right font-mono">{fmtNum(f.stock_actual, 2)}</td>
                  <td className="px-3 py-1.5 text-right font-mono text-amber-800">
                    {f.stock_comprometido > 0 ? fmtNum(f.stock_comprometido, 2) : "—"}
                  </td>
                  <td className="px-3 py-1.5 text-right font-mono">
                    {f.demanda > 0 ? fmtNum(f.demanda, 2) : "—"}
                  </td>
                  <td className={`px-3 py-1.5 text-right font-mono font-bold ${isFaltante ? "text-rose-800 text-[14px]" : "text-emerald-700"}`}>
                    {isFaltante ? fmtNum(f.faltante, 2) : (sobrante >= 0 ? `+${fmtNum(sobrante, 2)}` : fmtNum(sobrante, 2))}
                  </td>
                </tr>
                {isOpen && (f.pedidos_ref || []).length > 0 && (
                  <tr className="bg-neutral-50/60">
                    <td colSpan={7} className="px-6 py-2">
                      <div className="text-[11px] uppercase font-mono tracking-widest text-[#666] mb-1">Pedidos con demanda:</div>
                      <ul className="text-[12px] space-y-0.5">
                        {(f.pedidos_ref || []).map((r, i) => (
                          <li key={i} className="font-mono">
                            <span className="text-[#666]">{r.numero}</span>
                            <span className="mx-2">·</span>
                            <span>{r.cliente || "(sin cliente)"}</span>
                            <span className="mx-2">·</span>
                            <span className="font-bold">{fmtNum(r.cantidad, 2)} kg</span>
                          </li>
                        ))}
                      </ul>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
